// First-visit prompts.
//
// Two sequenced banners, both bottom-anchored, both dismissible:
//
//   1. STORAGE NOTICE  — first thing a new visitor sees. One line plus
//      "Got it" button. Sets `frozen-fly:storage-ack-v1` in localStorage
//      so it never shows again. Once acknowledged, the next banner shows.
//
//   2. LOCATION PROMPT — "want closer rivers first? show me where you are"
//      with a "Use my location" button (triggers the browser geolocation
//      prompt) or a "No thanks" dismiss. Either way sets a flag so we
//      don't pester on every visit.
//
// We never auto-trigger navigator.geolocation — always behind a user click.

import { requestLocation, getStoredLocation } from '../lib/geolocation';

const STORAGE_ACK_KEY = 'frozen-fly:storage-ack-v1';
const LOCATION_DISMISSED_KEY = 'frozen-fly:location-dismissed-v1';

export function hasAckedStorage(): boolean {
  try { return localStorage.getItem(STORAGE_ACK_KEY) === '1'; } catch { return true; }
}

export function ackStorage(): void {
  try { localStorage.setItem(STORAGE_ACK_KEY, '1'); } catch { /* ignore */ }
}

export function hasDismissedLocationPrompt(): boolean {
  try { return localStorage.getItem(LOCATION_DISMISSED_KEY) === '1'; } catch { return true; }
}

export function dismissLocationPrompt(): void {
  try { localStorage.setItem(LOCATION_DISMISSED_KEY, '1'); } catch { /* ignore */ }
}

/**
 * Show the right banner for the current state. Idempotent — safe to call
 * multiple times. Call once after boot, and again after any state change
 * (e.g. user acknowledged storage → show location prompt next).
 */
export function maybeShowConsentBanners(onChange?: () => void): void {
  removeExistingBanners();

  if (!hasAckedStorage()) {
    mountStorageBanner(() => {
      ackStorage();
      removeExistingBanners();
      // Sequence: after acking storage, see if we should offer location.
      maybeShowConsentBanners(onChange);
      onChange?.();
    });
    return;
  }

  // Storage already acked — consider the location prompt.
  if (!hasDismissedLocationPrompt() && !getStoredLocation()) {
    mountLocationBanner(
      async () => {
        const loc = await requestLocation();
        if (loc) dismissLocationPrompt(); // they accepted; don't re-ask
        removeExistingBanners();
        onChange?.();
      },
      () => {
        dismissLocationPrompt();
        removeExistingBanners();
        onChange?.();
      },
    );
  }
}

function removeExistingBanners(): void {
  document.querySelectorAll('.consent-banner').forEach((el) => el.remove());
}

function mountStorageBanner(onAccept: () => void): void {
  const el = document.createElement('div');
  el.className = 'consent-banner consent-banner-storage';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Storage notice');
  el.innerHTML = `
    <div class="consent-banner-body">
      <div class="consent-banner-text">
        <strong>This site uses browser storage to remember your filter preferences.</strong>
        <span>No analytics, no third-party cookies, no tracking.
          <a href="#/privacy" class="link">Privacy</a> ·
          <a href="#/cookies" class="link">Cookies & Storage</a>
        </span>
      </div>
      <div class="consent-banner-actions">
        <button class="btn btn-sm btn-primary" data-action="ack">Got it</button>
      </div>
    </div>
  `;
  document.body.appendChild(el);
  el.querySelector<HTMLButtonElement>('[data-action="ack"]')?.addEventListener('click', onAccept);
  // Slide-up animation on next frame.
  requestAnimationFrame(() => el.classList.add('is-shown'));
}

function mountLocationBanner(onAccept: () => void, onDismiss: () => void): void {
  const el = document.createElement('div');
  el.className = 'consent-banner consent-banner-location';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Location request');
  el.innerHTML = `
    <div class="consent-banner-body">
      <div class="consent-banner-text">
        <strong>📍 See rivers near you?</strong>
        <span>We can sort by proximity — your browser asks, the location stays in your browser (never sent to us).
          <a href="#/privacy" class="link">Details</a>
        </span>
      </div>
      <div class="consent-banner-actions">
        <button class="btn btn-sm btn-ghost" data-action="dismiss">No thanks</button>
        <button class="btn btn-sm btn-primary" data-action="accept">Use my location</button>
      </div>
    </div>
  `;
  document.body.appendChild(el);
  el.querySelector<HTMLButtonElement>('[data-action="accept"]')?.addEventListener('click', onAccept);
  el.querySelector<HTMLButtonElement>('[data-action="dismiss"]')?.addEventListener('click', onDismiss);
  requestAnimationFrame(() => el.classList.add('is-shown'));
}
